import type { Medal } from "@seagames/contracts";
import { useTranslation } from "react-i18next";

/**
 * The medal colours, one copy for every screen: the word (or its letter)
 * carries the meaning, the disc helps the eye — never colour alone. The hues
 * are tokens (`--color-medal-*`, styles.css), validated for the charts.
 */
export const MEDAL_DISC: Record<Medal, string> = {
	GOLD: "bg-medal-gold",
	SILVER: "bg-medal-silver",
	BRONZE: "bg-medal-bronze",
};

/** The CSS colour of a medal — for SVG fills and inline widths. */
export const MEDAL_COLOR: Record<Medal, string> = {
	GOLD: "var(--color-medal-gold)",
	SILVER: "var(--color-medal-silver)",
	BRONZE: "var(--color-medal-bronze)",
};

/** G / S / B — the letter on a disc, so a medal reads in black and white too. */
export const MEDAL_LETTER: Record<Medal, string> = { GOLD: "G", SILVER: "S", BRONZE: "B" };

const WASH: Record<Medal, string> = {
	GOLD: "bg-medal-gold-wash text-ink ring-medal-gold-ring",
	SILVER: "bg-medal-silver-wash text-ink ring-medal-silver-ring",
	BRONZE: "bg-medal-bronze-wash text-ink ring-medal-bronze-ring",
};

/** A small disc in the medal's colour (header cells, totals). */
export function MedalDisc({ medal, className = "" }: { medal: Medal; className?: string }) {
	return <span aria-hidden="true" className={`inline-block size-[0.625rem] shrink-0 rounded-full ${MEDAL_DISC[medal]} ${className}`} />;
}

/** A disc with its letter (G / S / B), white on the colour — podium chips and dense rows. */
export function MedalBadge({ medal, className = "size-[1.375rem] text-[0.6875rem]" }: { medal: Medal; className?: string }) {
	const { t } = useTranslation("medals");
	return (
		<span
			role="img"
			aria-label={t(`medal.${medal}`)}
			className={`inline-flex shrink-0 items-center justify-center rounded-full font-black text-white leading-none ${MEDAL_DISC[medal]} ${className}`}
		>
			{MEDAL_LETTER[medal]}
		</span>
	);
}

/** "● Gold" as a pill — the medal column of the list. */
export function MedalChip({ medal }: { medal: Medal }) {
	const { t } = useTranslation("medals");
	return (
		<span className={`inline-flex items-center gap-[0.375rem] whitespace-nowrap rounded-full px-[0.5rem] py-[0.125rem] font-bold text-[0.8125rem] ring-1 ring-inset ${WASH[medal]}`}>
			<MedalDisc medal={medal} />
			{t(`medal.${medal}`)}
		</span>
	);
}
